import React from "react";
import { useTranslation } from "react-i18next";
import { MdSchedule } from "react-icons/md";
import CarrierLogo from "../CarrierLogo";
import { INTEGRATION } from "./upcomingCarriers";

const LegendDot = ({ className, label }) => (
  <span className="flex items-center gap-1.5">
    <span className={`h-2 w-2 rounded-full ${className}`} />
    {label}
  </span>
);

const UpcomingCard = ({ carrier }) => {
  const { t } = useTranslation("settings");
  const inIntegration = carrier.status === INTEGRATION;

  return (
    <div
      className={`flex items-start gap-2.5 rounded-[10px] border px-2.5 py-2 sm:px-[16px] sm:py-2.5 ${
        inIntegration
          ? "border-state-info-border bg-ui-surface"
          : "border-ui-border bg-ui-surface-sunken"
      }`}
    >
      <div className="hidden sm:block">
        <CarrierLogo
          src={carrier.logo}
          name={carrier.name}
          code={carrier.code}
          size="md"
          tone="muted"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <div className="text-[13px] font-semibold leading-snug text-ui-text">{carrier.name}</div>
        <div className="flex items-center gap-[5px] text-[11px] leading-snug text-ui-text-muted">
          <span
            className={`h-1.5 w-1.5 shrink-0 rounded-full ${
              inIntegration ? "bg-brand" : "bg-ui-text-subtle"
            }`}
          />
          {inIntegration ? t("carriers.upcoming.integration") : t("carriers.upcoming.planned")}
        </div>
      </div>
    </div>
  );
};

const ComingSoonSection = ({ groups, searching }) => {
  const { t } = useTranslation("settings");
  const count = groups.reduce((sum, group) => sum + group.items.length, 0);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2.5">
            <MdSchedule className="h-[18px] w-[18px] text-brand" />
            <h2 className="m-0 text-base font-semibold text-ui-text">{t("carriers.upcoming.title")}</h2>
            <span className="rounded-full bg-brand-light px-2 py-px text-[11px] font-semibold text-brand">
              {count}
            </span>
          </div>
          <p className="mb-0 mt-1.5 text-xs text-ui-text-muted sm:mb-4">
            {t("carriers.upcoming.intro")}
          </p>
        </div>
        <div className="flex items-center gap-3.5 text-[11px] text-ui-text-muted">
          <LegendDot className="bg-brand" label={t("carriers.upcoming.integration")} />
          <LegendDot className="bg-ui-text-subtle" label={t("carriers.upcoming.planned")} />
        </div>
      </div>

      {groups.map((group) => (
        <div key={group.title} className="flex flex-col gap-2.5">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-state-neutral-text">
              {t(group.i18nKey, { defaultValue: group.title })}
            </span>
            <span className="text-[11px] text-ui-text-subtle">{group.items.length}</span>
            <span className="h-px flex-1 bg-ui-border" />
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-[repeat(auto-fill,minmax(250px,1fr))] sm:gap-2.5">
            {group.items.map((carrier) => (
              <UpcomingCard key={carrier.name} carrier={carrier} />
            ))}
          </div>
        </div>
      ))}

      {!count && (
        <div className="rounded-xl border border-dashed border-ui-border bg-ui-surface p-5 text-center text-[13px] text-ui-text-muted">
          {searching
            ? t("carriers.upcoming.noMatch")
            : t("carriers.upcoming.none")}
        </div>
      )}
    </section>
  );
};

export default ComingSoonSection;
